import React from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  AlertTitle,
  Container 
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon 
} from '@mui/icons-material';

const RouteError: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  // Determine error type and message
  let errorTitle = 'Something went wrong';
  let errorMessage = 'An unexpected error occurred while loading this page.';
  let errorDetails = '';
  let isNotFound = false;

  if (isRouteErrorResponse(error)) {
    // Handle React Router errors
    if (error.status === 404) {
      errorTitle = 'Page Not Found';
      errorMessage = 'The page you\'re looking for doesn\'t exist.';
      isNotFound = true;
    } else {
      errorTitle = `Error ${error.status}`;
      errorMessage = error.statusText || 'An error occurred while loading this page.';
      errorDetails = error.data?.message || '';
    }
  } else if (error instanceof Error) {
    // Handle JavaScript errors
    errorTitle = 'Application Error';
    errorMessage = error.message || 'An unexpected error occurred.';
    errorDetails = error.stack || '';
  } else if (typeof error === 'string') {
    // Handle string errors
    errorMessage = error;
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 600,
            textAlign: 'center',
            borderRadius: 2,
            width: '100%'
          }}
        >
          <WarningIcon 
            sx={{ 
              fontSize: 64, 
              color: isNotFound ? 'warning.main' : 'error.main', 
              mb: 2 
            }} 
          />
          
          <Typography variant="h4" gutterBottom color={isNotFound ? 'warning.main' : 'error.main'}>
            {errorTitle}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {errorMessage}
          </Typography>

          {errorDetails && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <AlertTitle>Error Details</AlertTitle>
              {errorDetails}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ minWidth: 120 }}
            >
              Refresh Page
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{ minWidth: 120 }}
            >
              Go Back
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{ minWidth: 120 }}
            >
              Go Home
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && errorDetails && (
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="h6" gutterBottom>
                Stack Trace (Development Only)
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f8f9fa',
                  maxHeight: 200,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {errorDetails}
                </pre>
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default RouteError; 